// Avoid use of flow due to a buggy testing
import Swal from 'sweetalert2';
import React from 'react';
import ReactDOM from 'react-dom';

import { Icon } from '@elements';
import { checkIfIsMobile } from './helpers_index/layout';
import { isIOSApp } from './helpers_index/basic';

const _ = (v: any) => window.i18n.t(v);

// Where the app shows its phone layout (checkIfIsMobile: the phone, and an iPad in portrait) a dialog looks like
// the platform's own (AppLayout.scss, .nativeDialog): an iOS alert in the iOS app, a Material 3 dialog on
// Android. The web and the computer layout keep the old dialog. Decided when the dialog opens.
const phonePlatform = (): ?string => {
  if (!checkIfIsMobile({ window: { width: window.innerWidth } })) return null;
  // isCordovaIos: the iOS build's index.html (and the force-ios test switch); isIOSApp: Cordova's platform id
  return window.isCordovaIos || isIOSApp() ? 'ios' : 'android';
};

// A native alert has a title and a message. A text of more than one sentence ("Há alterações não salvas. Você
// quer sair sem salvar?") shows the last sentence as the message and the rest as the title; one sentence is the
// title. A first part too short to be a sentence ("Dr.", "1.") isn't split off.
export const titleAndMessage = (text: string): [string, ?string] => {
  const value = String(text);
  const boundaries = Array.from(value.matchAll(/[.?!]\s+(?=\S)/g));
  const split = boundaries.length ? boundaries[boundaries.length - 1].index + 1 : -1;
  return split >= 12 ? [value.slice(0, split), value.slice(split).trim()] : [value, null];
};

// The text of a phone dialog. Native dialogs have no big icon: the warning triangle and the decorative icons
// go. Only the record tip (recordFriendlyAlert) keeps its friendly illustration, small, above the title.
const NativeDialogText = ({ i18nKey, icon, iconFill }: any) => {
  const [title, message] = titleAndMessage(_(i18nKey));
  return (
    <div>
      {icon && <Icon iconName={icon} size={40} {...(iconFill ? { fill: iconFill } : {})} />}
      <div className="nativeDialogTitle" id="nativeDialogTitle">{title}</div>
      {message && <div className="nativeDialogMessage" id="nativeDialogMessage">{message}</div>}
    </div>
  );
};

// Before SweetAlert shows the dialog, so nothing jumps:
// - two buttons sit side by side; when a label doesn't fit on one line they stack, as on the platforms, with the
//   action on top and first in the page too, so screen readers and the keyboard meet them in the order they're seen;
// - the title names the dialog and the message describes it (SweetAlert's own title is empty).
const prepareNativeDialog = (popup: HTMLElement) => {
  popup.style.display = 'grid';
  popup.classList.add('measuring');
  const actions: any = popup.querySelector('.swal2-actions');
  const stacked = Array.from(actions.querySelectorAll('button')).some((button: any) => button.scrollWidth > button.clientWidth);
  popup.classList.remove('measuring');
  popup.classList.toggle('stacked', stacked);
  if (stacked) actions.insertBefore(popup.querySelector('.swal2-confirm'), actions.firstChild);

  popup.setAttribute('aria-labelledby', 'nativeDialogTitle');
  if (popup.querySelector('#nativeDialogMessage')) popup.setAttribute('aria-describedby', 'nativeDialogMessage');
  else popup.removeAttribute('aria-describedby');
};

// The phone dialog's options. destructive: the confirmation deletes or discards something (Sim in confirmI18n),
// in red. The preferred answer, semibold on iOS, is the one the dialog focuses: Não when it focuses the cancel.
const nativeDialog = (platform: string, options: Object, html: string, destructive: boolean = false): Object => {
  const cancelPreferred = Boolean(options.showCancelButton && options.focusCancel);
  return {
    ...options,
    html,
    buttonsStyling: false,
    // the platforms' order: the dismissive answer on the left, the action on the right
    reverseButtons: true,
    // an iOS alert ignores a tap beside it (Android's dialogs close, as the caller chose)
    ...(platform === 'ios' ? { allowOutsideClick: false } : {}),
    customClass: {
      container: `nativeDialog ${platform}Dialog${options.showCancelButton ? ' twoButtons' : ''}`,
      confirmButton: [destructive && 'destructive', !cancelPreferred && 'preferred'].filter(Boolean).join(' '),
      cancelButton: cancelPreferred ? 'preferred' : '',
    },
    willOpen: prepareNativeDialog,
  };
};

// Opens the dialog: the platform's look on the phone, the old dialog elsewhere
const fire = (platform: ?string, options: Object, htmlContent: HTMLElement, destructive?: boolean) => Swal.fire(platform
  ? nativeDialog(platform, options, htmlContent.innerHTML, destructive)
  : { ...options, html: `<div>${htmlContent.innerHTML}</div>` });

const SwalContenti18nTextRender = ({ i18nKey }: any) => <span>{_(i18nKey)}</span>;

const i18nTextAlert = (textI18n: string) => {
  const platform = phonePlatform();
  const htmlContent = document.createElement('div');
  ReactDOM.render(platform ? <NativeDialogText i18nKey={`alerts:${textI18n}`} /> : (
    <div>
      <SwalContenti18nTextRender i18nKey={`alerts:${textI18n}`} />
    </div>
  ), htmlContent, () => {
    const options = {
      confirmButtonText: _('alerts:button/ok'),
      customClass: {
        confirmButton: 'btn primary',
      },
    };
    fire(platform, options, htmlContent);
  });
};

const recordFriendlyAlert = (textI18n: string, icon: string = 'color-timeline', iconFill?: string) => new Promise(resolve => {
  const platform = phonePlatform();
  const htmlContent = document.createElement('div');
  ReactDOM.render(platform ? <NativeDialogText i18nKey={`alerts:${textI18n}`} icon={icon} iconFill={iconFill} /> : (
    <div>
      <Icon iconName={icon} size={128} {...(iconFill ? { fill: iconFill } : {})} />
      <div className="textContent">
        <br />
        <SwalContenti18nTextRender i18nKey={`alerts:${textI18n}`} />
      </div>
    </div>
  ), htmlContent, () => {
    const options = {
      confirmButtonText: _('alerts:button/ok'),
      customClass: {
        confirmButton: 'btn primary',
      },
    };
    fire(platform, options, htmlContent).then(() => resolve());
  });
});

// allowOutsideClick false: a click beside the dialog (the second click of a double click) doesn't count as "Não"
const confirmI18n = (
  textI18n: string, icon?: string, iconFill: string = '#000', focusCancel?: boolean = true, allowOutsideClick?: boolean = true,
): Promise<boolean> => new Promise(resolve => {
  const platform = phonePlatform();
  const htmlContent = document.createElement('div');
  ReactDOM.render(platform ? <NativeDialogText i18nKey={`alerts:${textI18n}`} /> : (
    <div>
      {icon && <div style={{ width: '100%' }}><Icon iconName={icon} size={128} {...(iconFill ? { fill: iconFill } : {})} /></div>}
      <SwalContenti18nTextRender i18nKey={`alerts:${textI18n}`} />
    </div>
  ), htmlContent, () => {
    const options = {
      showCancelButton: true,
      confirmButtonText: _('alerts:button/yes'),
      cancelButtonText: _('alerts:button/no'),
      reverseButtons: true,
      focusCancel,
      allowOutsideClick,
      customClass: {
        confirmButton: 'btn danger',
        cancelButton: 'btn',
      },
    };
    fire(platform, options, htmlContent, true).then(({ value }) => {
      resolve(value);
    }).catch(() => resolve(false));
  });
});

// "Tem certeza que deseja excluir…?": the second click of a double click on Excluir lands beside the dialog
// and must not count as "Não"
const confirmDelete = (textI18n: string): Promise<boolean> => confirmI18n(textI18n, 'warning', '#FFA433', true, false);

const customConfirmI18n = (textI18n: string,
  confirmButtonText: string,
  cancelButtonText: string,
  icon?: string,
  iconFill: string = '#000',
  focusCancel?: boolean = true,
): Promise<boolean> => new Promise(resolve => {
  const platform = phonePlatform();
  const htmlContent = document.createElement('div');
  ReactDOM.render(platform ? <NativeDialogText i18nKey={`alerts:${textI18n}`} /> : (
    <div>
      {icon && <div style={{ width: '100%' }}><Icon iconName={icon} size={128} {...(iconFill ? { fill: iconFill } : {})} /></div>}
      <SwalContenti18nTextRender i18nKey={`alerts:${textI18n}`} />
    </div>
  ), htmlContent, () => {
    const options = {
      showCancelButton: true,
      confirmButtonText: _(`alerts:${confirmButtonText}`),
      cancelButtonText: _(`alerts:${cancelButtonText}`),
      reverseButtons: true,
      focusCancel,
    };
    fire(platform, options, htmlContent).then(({ value }) => {
      resolve(value);
    }).catch(() => resolve(false));
  });
});

const info = (textI18n: string, icon?: string, iconFill: string = '#000'): Promise<boolean> => new Promise(resolve => {
  const platform = phonePlatform();
  const htmlContent = document.createElement('div');
  ReactDOM.render(platform ? <NativeDialogText i18nKey={`alerts:${textI18n}`} /> : (
    <div>
      {icon && <div style={{ width: '100%' }}><Icon iconName={icon} size={128} {...(iconFill ? { fill: iconFill } : {})} /></div>}
      <SwalContenti18nTextRender i18nKey={`alerts:${textI18n}`} />
    </div>
  ), htmlContent, () => {
    const options = {
      showCancelButton: false,
      confirmButtonText: _('alerts:button/ok'),
    };
    fire(platform, options, htmlContent).then(({ value }) => {
      resolve(value);
    }).catch(() => resolve(false));
  });
});


export default {
  i18nTextAlert, recordFriendlyAlert, confirmI18n, confirmDelete, info, customConfirmI18n,
};
